import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getApiError } from "@/lib/api";
import { fmtLog } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldCheck, Loader2, Play, Square, ChevronDown, ChevronRight, FileText, AlertTriangle, Wand2, FilePlus2,
} from "lucide-react";
import { DOCS_ACCENT as ACCENT } from "@/lib/govDocs";

const SEV = { high: "bg-rose-50 text-rose-700 border-rose-200", medium: "bg-amber-50 text-amber-700 border-amber-200", low: "bg-slate-50 text-slate-600 border-slate-200" };
const COVERAGE = {
  covered: { label: "Covered", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partial: { label: "Partly covered", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  missing: { label: "Not covered", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
const PRIORITY = { high: "text-rose-700", medium: "text-amber-700", low: "text-slate-500" };
const fmtDay = (d) => (d ? d.split("-").reverse().join("/") : "");

function Card({ title, icon: Icon = ShieldCheck, action, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mt-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2"><Icon className="h-4 w-4" style={{ color: ACCENT }} />
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{title}</h2></div>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Progress / status of the latest run of one check, with start and stop buttons. */
function RunBar({ run, startLabel, onStart, onCancel, starting, disabled, estimate }) {
  const running = run?.status === "running";
  const pct = run?.total ? Math.round((100 * run.done) / run.total) : 0;
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-3">
        {running ? (
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onCancel} data-testid="compliance-cancel">
            <Square className="h-3.5 w-3.5 mr-1" />Stop
          </Button>
        ) : (
          <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: ACCENT }} onClick={onStart} disabled={starting || disabled} data-testid="compliance-start">
            {starting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}{startLabel}
          </Button>
        )}
        <div className="text-xs text-slate-600 flex-1 min-w-[200px]">
          {running && <>Checking {run.done} of {run.total}… You can leave this page; it keeps going.</>}
          {!running && run?.status === "done" && <>Last run {fmtLog(run.finished_at)} by {run.by_name}: {run.done} checked.</>}
          {run?.status === "cancelled" && <>Stopped {fmtLog(run.finished_at)} after {run.done} of {run.total}.</>}
          {run?.status === "interrupted" && <span className="text-amber-700">The last run was interrupted (the app restarted) after {run.done} of {run.total}. Run it again.</span>}
          {run?.status === "failed" && <span className="text-rose-700">The last run failed: {run.error}</span>}
          {!run && <span className="text-slate-500">{estimate}</span>}
        </div>
      </div>
      {running && (
        <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: ACCENT }} /></div>
      )}
      {run?.failed?.length > 0 && (
        <div className="text-[11px] text-amber-700 mt-1.5">{run.failed.length} couldn't be checked: {run.failed.map((f) => `${f.item} (${f.error})`).join("; ")}</div>
      )}
    </div>
  );
}

function ReviewsTab() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [libs, setLibs] = useState({ policies: true, company: false });
  const [starting, setStarting] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [busyDoc, setBusyDoc] = useState(null);
  const load = useCallback(() => api.govComplianceReviews().then(setData).catch((e) => toast.error(getApiError(e, "Couldn't load reviews."))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.run?.status !== "running") return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data?.run?.status, load]);

  const start = async () => {
    const chosen = Object.keys(libs).filter((k) => libs[k]);
    if (!window.confirm("Review every Active document in the chosen libraries with AI? It takes about half a minute per document, runs in the background, and costs roughly 10–15 cents per document.")) return;
    setStarting(true);
    try { await api.govRunComplianceReviews(chosen); toast.success("Review started"); await load(); }
    catch (e) { toast.error(getApiError(e, "Couldn't start the review.")); }
    finally { setStarting(false); }
  };
  const improve = async (r) => {
    setBusyDoc(r.document_id);
    try {
      await api.govComplianceStartDraft(r.document_id);
      nav(`/documents/${r.library}/${r.document_id}?tab=draft`);
    } catch (e) { toast.error(getApiError(e, "Couldn't start a draft.")); setBusyDoc(null); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const totals = data.results.reduce((a, r) => ({ high: a.high + (r.counts.high || 0), medium: a.medium + (r.counts.medium || 0) }), { high: 0, medium: 0 });
  return (
    <>
      <Card title="Review every Active document" icon={Wand2} testid="compliance-reviews-run">
        <p className="text-xs text-slate-500 mb-3">
          Runs the same AI review you use on drafts against each Active document, checking it against our other documents and
          NDIS Commission guidance. Nothing is changed: use <b>Start improving</b> to open a new draft with that document's
          suggestions, ready to discuss, apply or dismiss.
        </p>
        <div className="flex gap-4 mb-3">
          {[["policies", "Policies & Procedures"], ["company", "Company Documents"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={libs[k]} onCheckedChange={(v) => setLibs((x) => ({ ...x, [k]: !!v }))} disabled={data.run?.status === "running"} />{label}
            </label>
          ))}
        </div>
        <RunBar run={data.run} startLabel="Review all" onStart={start} starting={starting} disabled={!libs.policies && !libs.company}
          onCancel={() => api.govCancelCompliance("reviews").then(load).catch((e) => toast.error(getApiError(e, "Couldn't stop it.")))}
          estimate={`${data.active_documents} Active document${data.active_documents !== 1 ? "s" : ""} across both libraries.`} />
      </Card>

      <Card title="Results" icon={ShieldCheck} testid="compliance-reviews-results">
        {!data.results.length ? (
          <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">No documents have been reviewed yet.</div>
        ) : (
          <>
            <div className="text-xs text-slate-600 mb-2">{data.results.length} documents reviewed · {totals.high} high and {totals.medium} medium priority suggestions. Most urgent first.</div>
            <div className="divide-y divide-slate-100">
              {data.results.map((r) => (
                <div key={r.document_id} className="py-2.5" data-testid={`compliance-row-${r.document_id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <button className="text-slate-400" onClick={() => setOpenRow(openRow === r.document_id ? null : r.document_id)}>
                      {openRow === r.document_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <Link to={`/documents/${r.library}/${r.document_id}`} className="text-sm font-medium text-slate-800 hover:underline flex-1 min-w-[200px]">{r.label}</Link>
                    {["high", "medium", "low"].map((s) => (
                      <span key={s} className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${SEV[s]}`}>{r.counts[s] || 0} {s}</span>
                    ))}
                    {r.out_of_date ? (
                      <span className="text-[11px] text-amber-700 w-32 text-right">Newer version since</span>
                    ) : r.has_draft ? (
                      <Button size="sm" variant="outline" className="h-7 text-xs w-32" onClick={() => nav(`/documents/${r.library}/${r.document_id}?tab=draft`)}>Open draft</Button>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs w-32" onClick={() => improve(r)} disabled={busyDoc === r.document_id} data-testid="compliance-improve">
                        {busyDoc === r.document_id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Wand2 className="h-3 w-3 mr-1" />}Start improving
                      </Button>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 ml-6">Version {r.version || "—"} · reviewed {fmtLog(r.at)}</div>
                  {openRow === r.document_id && (
                    <div className="ml-6 mt-2 space-y-1.5">
                      {r.summary && <p className="text-xs text-slate-700">{r.summary}</p>}
                      {r.suggestions.map((s, i) => (
                        <div key={i} className="text-xs text-slate-600 flex gap-2">
                          <span className={`text-[9px] font-semibold uppercase px-1 py-0.5 rounded border h-fit ${SEV[s.severity]}`}>{s.severity}</span>
                          <span>{s.section ? <span className="text-slate-400">{s.section}: </span> : null}{s.issue}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </>
  );
}

function CoverageTab() {
  const [data, setData] = useState(null);
  const [modules, setModules] = useState(null);
  const [starting, setStarting] = useState(false);
  const [openStd, setOpenStd] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => api.govComplianceCoverage().then((d) => { setData(d); setModules((m) => m ?? d.settings.modules); })
    .catch((e) => toast.error(getApiError(e, "Couldn't load coverage."))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.run?.status !== "running") return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data?.run?.status, load]);

  const toggle = (name, on) => setModules((m) => (on ? [...m, name] : m.filter((x) => x !== name)));
  const start = async () => {
    setStarting(true);
    try {
      await api.govComplianceSettings(modules);
      await api.govRunComplianceCoverage();
      toast.success("Coverage check started");
      await load();
    } catch (e) { toast.error(getApiError(e, "Couldn't start the check.")); }
    finally { setStarting(false); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (!data.available) {
    return (
      <Card title="Practice Standards coverage" testid="coverage-unavailable">
        <div className="text-xs text-slate-500">The NDIS Practice Standards haven't been downloaded yet. They come from the NDIS Commission check: see <Link to="/documents/ndis-commission" className="underline">NDIS Commission</Link>.</div>
      </Card>
    );
  }
  const rows = data.standards;
  const count = (c) => rows.filter((r) => r.coverage === c).length;
  const running = data.run?.status === "running";
  const selected = data.groups.filter((g) => (modules || []).includes(g.name)).reduce((a, g) => a + g.standards, 0);
  const shown = filter === "all" ? rows : rows.filter((r) => r.coverage === filter);
  const byModule = shown.reduce((acc, r) => { (acc[r.module] = acc[r.module] || []).push(r); return acc; }, {});
  return (
    <>
      <Card title="Check our documents against the NDIS Practice Standards" icon={ShieldCheck} testid="coverage-run">
        <p className="text-xs text-slate-500 mb-3">
          For each standard, the AI looks for our Active documents that cover it and judges whether its outcome and quality
          indicators are covered. It then lists policies we may be missing. Tick the modules that apply to our registration.
        </p>
        <div className="grid sm:grid-cols-2 gap-1.5 mb-3">
          {data.groups.map((g) => (
            <label key={g.name} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={(modules || []).includes(g.name)} onCheckedChange={(v) => toggle(g.name, !!v)} disabled={running} />
              {g.name} <span className="text-[11px] text-slate-400">({g.standards})</span>
            </label>
          ))}
        </div>
        <RunBar run={data.run} startLabel="Check coverage" onStart={start} starting={starting} disabled={!selected}
          onCancel={() => api.govCancelCompliance("coverage").then(load).catch((e) => toast.error(getApiError(e, "Couldn't stop it.")))}
          estimate={`${selected} standards selected. Takes a few minutes and costs well under a dollar.`} />
        {data.sources.length > 0 && (
          <div className="text-[11px] text-slate-400 mt-2">
            Standards from {data.sources.map((s, i) => (
              <span key={s.url}>{i > 0 && ", "}<a href={s.url} target="_blank" rel="noopener noreferrer" className="underline">{s.title}</a>{s.updated && ` (updated ${fmtDay(s.updated)})`}</span>
            ))} on the NDIS Commission website.
          </div>
        )}
      </Card>

      {data.run?.missing_policies?.length > 0 && (
        <Card title="Policies we may be missing" icon={FilePlus2} testid="coverage-missing">
          <div className="space-y-2.5">
            {data.run.missing_policies.map((m, i) => (
              <div key={i} className="text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">{m.title}</span>
                  <span className={`text-[10px] font-semibold uppercase ${PRIORITY[m.priority]}`}>{m.priority} priority</span>
                </div>
                {m.purpose && <div className="text-slate-600">{m.purpose}</div>}
                {m.standards.length > 0 && <div className="text-slate-400">Addresses: {m.standards.join(", ")}</div>}
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-400 mt-3">Suggestions from AI, based on the standards our documents don't fully cover. Check them before creating anything.</div>
        </Card>
      )}

      {rows.length > 0 && (
        <Card title="Standards" icon={ShieldCheck} testid="coverage-standards" action={
          <div className="flex gap-1">
            {[["all", `All ${rows.length}`], ["missing", `Not covered ${count("missing")}`], ["partial", `Partly ${count("partial")}`], ["covered", `Covered ${count("covered")}`]].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} className={`text-[11px] px-2 py-0.5 rounded-full border ${filter === k ? "text-white border-transparent" : "text-slate-600 border-slate-200"}`}
                style={filter === k ? { backgroundColor: ACCENT } : undefined} data-testid={`coverage-filter-${k}`}>{label}</button>
            ))}
          </div>
        }>
          {Object.entries(byModule).map(([mod, list]) => (
            <div key={mod} className="mb-3">
              <div className="text-[11px] font-semibold text-slate-500 mb-1">{mod}</div>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                {list.map((r) => (
                  <div key={r.id} className="px-3 py-2" data-testid={`coverage-${r.id}`}>
                    <button className="w-full flex items-center gap-2 text-left" onClick={() => setOpenStd(openStd === r.id ? null : r.id)}>
                      {openStd === r.id ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                      <span className="text-sm text-slate-800 flex-1">{r.name}</span>
                      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${COVERAGE[r.coverage].cls}`}>{COVERAGE[r.coverage].label}</span>
                    </button>
                    {openStd === r.id && (
                      <div className="ml-6 mt-1.5 space-y-1.5 text-xs">
                        {r.outcome && <div className="text-slate-500"><b>Outcome:</b> {r.outcome}</div>}
                        {r.summary && <div className="text-slate-700">{r.summary}</div>}
                        {r.documents.length > 0 && (
                          <div className="flex flex-wrap gap-2">{r.documents.map((d) => (
                            <Link key={d.document_id} to={`/documents/${d.library}/${d.document_id}`} className="inline-flex items-center gap-1 hover:underline" style={{ color: ACCENT }}>
                              <FileText className="h-3 w-3" />{d.label}
                            </Link>
                          ))}</div>
                        )}
                        {r.gaps.length > 0 && (
                          <div><div className="text-slate-600 font-medium">Not addressed:</div>
                            <ul className="list-disc ml-4 text-slate-600">{r.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul></div>
                        )}
                        {r.suggested_policy && <div className="text-slate-600"><b>Suggested new document:</b> {r.suggested_policy.title}. {r.suggested_policy.scope}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Card>
      )}
    </>
  );
}

export default function PolicyCompliance() {
  const [tab, setTab] = useState("reviews");
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="policy-compliance">
      <div className="flex items-center gap-2.5">
        <ShieldCheck className="h-5 w-5" style={{ color: ACCENT }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Compliance check</h1>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Check our Active documents against current NDIS Commission guidance and the NDIS Practice Standards. Results are AI
        suggestions to guide your review, not a compliance sign-off.
      </p>
      <div className="flex gap-1 border-b border-slate-200 mt-4">
        {[["reviews", "Document reviews"], ["coverage", "Practice Standards coverage"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`compliance-tab-${k}`}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === k ? "font-medium text-slate-800" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            style={tab === k ? { borderColor: ACCENT } : undefined}>{label}</button>
        ))}
      </div>
      {tab === "reviews" ? <ReviewsTab /> : <CoverageTab />}
      <p className="text-[11px] text-slate-400 mt-4 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />AI can miss things or misjudge them. A person should check each finding.
      </p>
    </div>
  );
}
